import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import MovieCard from '@/components/feature/MovieCard';
import { getMovieById, movies } from '@/mocks/movies';

export default function MovieDetailPage() {
  const { id } = useParams<{ id: string }>();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16 px-4 md:px-8 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-24 h-24 mx-auto flex items-center justify-center rounded-full bg-cinema-card mb-6">
              <i className="ri-error-warning-line text-4xl text-cinema-text-dim"></i>
            </div>
            <h1 className="text-2xl font-heading font-bold text-white mb-2">电影未找到</h1>
            <p className="text-cinema-text-dim mb-6">抱歉，您要查看的电影不存在或已下架</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-cinema-gold text-cinema-black font-semibold rounded-full hover:bg-cinema-gold-light transition-colors text-sm cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && m.category.some((c) => movie.category.includes(c)))
    .slice(0, 5);

  const movieSchema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    dateCreated: movie.releaseDate,
    director: {
      '@type': 'Person',
      name: movie.director,
    },
    actor: movie.cast.map((name) => ({
      '@type': 'Person',
      name,
    })),
    countryOfOrigin: movie.region,
    inLanguage: movie.language,
    duration: movie.duration,
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://8090movie.com/' },
      { '@type': 'ListItem', position: 2, name: movie.category[0] === 'huayu' ? '华语电影' : movie.category[0] === 'oumei' ? '欧美电影' : movie.category[0] === 'rihan' ? '日韩电影' : '电影分类', item: `https://8090movie.com/category/${movie.category[0]}` },
      { '@type': 'ListItem', position: 3, name: movie.title },
    ],
  };

  const categoryLabel = movie.category[0] === 'huayu' ? '华语电影' : movie.category[0] === 'oumei' ? '欧美电影' : movie.category[0] === 'rihan' ? '日韩电影' : '电影';

  return (
    <>
      <SEOHead
        title={`${movie.title} - 8090影视高清在线观看 | ${movie.year}年${categoryLabel}`}
        description={`${movie.description} ${movie.title}由${movie.director}执导，${movie.cast.slice(0, 4).join('、')}主演，8090影视电影大全高清在线观看。`}
        keywords={`${movie.title},8090影视,电影大全,${categoryLabel},高清电影,${movie.director},${movie.cast.slice(0, 3).join(',')},电影在线观看`}
        canonicalUrl={`https://8090movie.com/movie/${movie.id}`}
        ogType="video.movie"
        schema={{ ...movieSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-20 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
          />

          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: categoryLabel, href: `/category/${movie.category[0]}` },
              { label: movie.title },
            ]}
          />

          <article className="mt-6">
            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              <div className="flex-shrink-0">
                <div className="w-56 md:w-64 aspect-[2/3] rounded-lg overflow-hidden bg-cinema-card">
                  <img
                    src={movie.image}
                    alt={movie.title}
                    title={`${movie.title} - 8090影视电影大全`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3 mb-3">
                  {movie.isNew && (
                    <span className="px-3 py-1 text-xs font-semibold bg-cinema-crimson text-white rounded-full">最新上映</span>
                  )}
                  {movie.isClassic && (
                    <span className="px-3 py-1 text-xs font-semibold bg-cinema-gold text-cinema-black rounded-full">经典电影</span>
                  )}
                  <span className="text-cinema-text-dim text-sm">{movie.year}</span>
                </div>

                <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                  {movie.title}
                </h1>

                <p className="text-cinema-text text-sm mb-6 leading-relaxed">
                  {movie.description}
                </p>

                <div className="flex items-center gap-2 mb-6">
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-cinema-gold"></i>
                    <span className="text-cinema-gold text-lg font-bold">{movie.rating}</span>
                  </div>
                  <span className="text-cinema-text-dim text-sm">/ 10</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">导演</span>
                    <span className="text-white text-sm">{movie.director}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">主演</span>
                    <span className="text-white text-sm">{movie.cast.join(' / ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">类型</span>
                    <span className="text-white text-sm">{movie.tags.join(' / ')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">地区</span>
                    <span className="text-white text-sm">{movie.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">语言</span>
                    <span className="text-white text-sm">{movie.language}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">片长</span>
                    <span className="text-white text-sm">{movie.duration}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-cinema-text-dim text-sm w-16 flex-shrink-0">上映</span>
                    <span className="text-white text-sm">{movie.releaseDate}</span>
                  </div>
                </div>

                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-cinema-crimson text-white font-semibold rounded-full hover:bg-cinema-crimson-light transition-colors text-sm cursor-pointer"
                >
                  <i className="ri-play-circle-line"></i>
                  立即观看
                </Link>
              </div>
            </div>

            <div className="mt-12">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-4">剧情简介</h2>
              <div className="bg-cinema-card rounded-lg p-6 border border-cinema-border">
                <p className="text-cinema-text-dim text-sm leading-relaxed whitespace-pre-line">
                  {movie.longDescription}
                </p>
              </div>
            </div>

            <div className="mt-14">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-white mb-6">相关推荐</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </div>
          </article>
        </div>
      </main>

      <Footer />
    </>
  );
}